import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';

// Dedicated to the parent portal — deliberately separate from
// admin/staffGuard in auth.guard.ts, which key off a "usertoken"/"loginMode"
// cookie pair specific to the web-admin login flow. A parent's session has
// no relationship to that cookie pair at all: it's authToken in
// localStorage, set by ParentLoginComponent.onLoginSuccess() after OTP
// verification (mirroring the parent mobile app's own AsyncStorage key of
// the same name). Mixing the two guards together would make each one's
// behavior depend on unrelated login state.

// Blocks the admin/staff "login" route from ever rendering on the parent
// subdomain. NewLoginWithDomainVerificationComponent (that route's
// component) unconditionally calls verifySchoolDomainDetails() from its
// constructor, which validates `sub` ("parent") as if it were a school
// domain, fails, and hard-redirects to loginStrings.localHostLoginUrl —
// hardcoded to "http://app.localhost:4200/#/login" (see newLogin.json) —
// or prodHostLoginUrl() ("//app." + domainSuffix + "/#/login") in
// production. That redirect is what "navigating to admin login and
// validating the domain" actually was: the guard on the "" route never
// even ran, because parent.<domainSuffix>/#/login matches this "login"
// route directly, which had no guard at all.
export const parentLoginRouteGuard: CanActivateFn = () => {
  const router = inject(Router);
  const sub = window.location.hostname.split('.')[0];
  if (['parent', 'dev-parent'].indexOf(sub) !== -1) {
    router.navigate([localStorage.getItem('authToken') ? '/parent/dashboard' : '/parent/login']);
    return false;
  }
  return true;
};

// The parent portal (every /parent/* route, login included) only exists on
// the dedicated parent hostname. On any other host — a school subdomain such
// as demo.<domain>, app.<domain>, plain localhost — it must not render, so
// those hosts fall through to the normal admin/staff login.
export const parentHostGuard: CanActivateFn = () => {
  const router = inject(Router);
  const sub = window.location.hostname.split('.')[0];
  if (['parent', 'dev-parent'].indexOf(sub) !== -1) {
    return true;
  }
  router.navigate(['/login']);
  return false;
};

// Gates /parent/dashboard, /parent/diary, etc. — redirects to the parent
// login screen when there's no active parent session.
export const parentAuthGuard: CanActivateFn = () => {
  const router = inject(Router);
  if (!localStorage.getItem('authToken')) {
    router.navigate(['/parent/login']);
    return false;
  }
  return true;
};

// Replaces the router's static { path: "", redirectTo: "/login" } so the
// bare domain root resolves per-hostname instead of always assuming the
// admin login. Without this, parent.<domainSuffix>/ would hit that static
// redirect and render the ADMIN login page instead of the parent portal —
// this was the actual root cause of "parent.domainsuffix opening another
// page" reported earlier.
export const parentHomeRedirectGuard: CanActivateFn = () => {
  const router = inject(Router);
  const sub = window.location.hostname.split('.')[0];
  if (['parent', 'dev-parent'].indexOf(sub) !== -1) {
    router.navigate([localStorage.getItem('authToken') ? '/parent/dashboard' : '/parent/login']);
  } else {
    router.navigate(['/login']);
  }
  return false;
};
